import React from "react";
import styled from "@emotion/styled";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  IconButton as MuiIconButton,
  Tooltip,
  Modal,
  Stack,
  useTheme,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";

import copy from "copy-to-clipboard";
import useAuth from "../../../hooks/useAuth";
import useLLMConfig from "../../../hooks/user/useLLMConfig";
import { SnackbarAlert, Paper, Button } from "../../../components/pages/common/styled";
import LLMConfigHeader from "./llmHeader";
import { formatDate } from "../../../utils/format";
import { UserType } from "../../../utils/constants";
import CreateAPITokenModal from "../../../components/pages/llm/guard/M_CreateAPIToken";
import ModalBox from "../../../components/common/ModalBox";

const IconButton = styled(MuiIconButton)`
  padding: 4px;
`;
const headCells = [
  { id: "name", alignment: "left", label: "Name" },
  { id: "created_at", alignment: "left", label: "Created Date" },
  { id: "expires_at", alignment: "left", label: "Expire Date" },
  { id: "action", alignment: "left", label: "Action" },
];

const EnhancedTableHead = () => {
  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            sx={{ padding: "8px" }}
            key={headCell.id}
            align={headCell.alignment}
            padding={headCell.disablePadding ? "none" : "normal"}
          >
            <Typography variant="tableHeader">{headCell.label}</Typography>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};
function SkeletonContent(props) {
  const { rowsPerPage } = props;
  var rowArray = [];
  for (let i = 0; i < rowsPerPage; i++) {
    rowArray.push(i);
  }
  var cellArray = [1, 2, 3, 4, 5];

  return (
    <TableBody>
      {rowArray.map((r) => (
        <TableRow key={"s_" + r}>
          {cellArray.map((c) => (
            <TableCell sx={{ padding: "16px 8px" }} key={"s_" + r + "_" + c}>
              <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
}
SkeletonContent.propTypes = { rowsPerPage: PropTypes.number.isRequired };

function LLMAPITokens() {
  const { configZone } = useParams();
  const zoneUid = configZone;
  const theme = useTheme();

  const {
    userType,
    isAuthenticated,
    zoneController,
    homeController,
    wafdashController,
    webzoneController,
    wafeventController,
    planController,
  } = useAuth();
  const { llmConfig, getLLMConfig, configLLMSetting, errMsg, setErr } = useLLMConfig();

  const [rows, setRows] = React.useState(null);

  const [curJti, setCurJti] = React.useState(null);
  const [createdToken, setCreatedToken] = React.useState("");
  const [copySuccess, setCopySuccess] = React.useState(false);

  // view token modal
  const [viewOpen, setViewOpen] = React.useState(false);
  const viewHandleOpen = () => setViewOpen(true);
  const viewHandleClose = () => {
    setCreatedToken("");
    setViewOpen(false);
  };

  // create token modal
  const [createOpen, setCreateOpen] = React.useState(false);
  const createHandleOpen = () => setCreateOpen(true);
  const createHandleClose = () => setCreateOpen(false);

  // revoke modal
  const [revokeOpen, setRevokeOpen] = React.useState(false);
  const revokeHandleOpen = () => setRevokeOpen(true);
  const revokeHandleClose = () => setRevokeOpen(false);

  React.useEffect(() => {
    if (isAuthenticated) {
      if (zoneController) zoneController.abort();
      if (homeController) homeController.abort();
      if (wafdashController) wafdashController.abort();
      if (webzoneController) webzoneController.abort();
      if (wafeventController) wafeventController.abort();
      if (planController) planController.abort();
      if (zoneUid) {
        getLLMConfig(zoneUid);
      }
    }
    setErr(null);
  }, [isAuthenticated, getLLMConfig, zoneUid, setErr]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (llmConfig) {
      setRows(
        llmConfig?.tokens
          ?.filter((token) => token?.enabled)
          ?.sort((a, b) => {
            if (a?.created_at < b?.created_at) return -1;
            if (a?.created_at > b?.created_at) return -1;
            return 0;
          })
      );
    }
  }, [llmConfig]);

  const [snackOpen, setSnackOpen] = React.useState(false);
  const [message, setMessage] = React.useState();
  const [success, setSuccess] = React.useState();
  const handleSnackClose = () => {
    setSnackOpen(false);
    setErr(null);
  };

  React.useEffect(() => {
    if (errMsg) {
      setSnackOpen(true);
      setMessage(errMsg);
      setSuccess("error");
    } else {
      setSnackOpen(false);
      setMessage(null);
    }
  }, [errMsg]);

  const handleCopy = (e) => {
    copy(createdToken);
    e.target.focus();
    setCopySuccess(true);
  };
  const handleOnTooltipClose = () => {
    setTimeout(() => {
      setCopySuccess(false);
    }, 100);
  };

  return (
    <React.Fragment>
      <LLMConfigHeader title={"LLM API Tokens"} url={"config/api_tokens"} />
      <Box sx={{ background: "white", borderRadius: "28px 28px 28px 28px", padding: "36px 24px" }}>
        <Grid container px={4} pb={6}>
          <Grid item>
            <Typography variant="h2" display="inline">
              LLM API Tokens
            </Typography>
          </Grid>
          <Grid item xs></Grid>
          <Grid item>
            {UserType.ORG_USER === userType ? (
              <></>
            ) : (
              <Button
                variant="contained"
                color="primary"
                sx={{ height: "100%" }}
                onClick={() => {
                  createHandleOpen();
                }}
                startIcon={<AddCircleOutlineIcon />}
              >
                Create API Token
              </Button>
            )}
          </Grid>
        </Grid>
        <Paper sx={{ maxWidth: "90vw", boxShadow: "none" }}>
          <TableContainer>
            <Table aria-labelledby="tableTitle" size={"medium"} aria-label="enhanced table">
              <EnhancedTableHead />
              {rows === null ? (
                <SkeletonContent rowsPerPage={3} />
              ) : (
                <>
                  {rows?.length === 0 ? (
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={9}>
                          <Typography textAlign="center">There are no API Tokens.</Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  ) : (
                    <TableBody>
                      {rows?.map((row, index) => {
                        return (
                          <TableRow hover tabIndex={-1} key={`${row?.id}-${index}`}>
                            <TableCell align="left" sx={{ padding: "8px" }}>
                              {row?.name}
                            </TableCell>
                            <TableCell align="left" sx={{ padding: "8px" }}>
                              {formatDate(row?.created_at)}
                            </TableCell>
                            <TableCell align="left" sx={{ padding: "8px" }}>
                              <Typography color={new Date(row?.expires_at) > new Date() ? theme.palette.custom.green.main : "error"}>{formatDate(row?.expires_at)}</Typography>
                            </TableCell>
                            <TableCell align="left" sx={{ padding: "8px" }}>
                              <Tooltip title="Revoke">
                                <IconButton
                                  size="large"
                                  id="demo-customized-button"
                                  aria-haspopup="true"
                                  onClick={() => {
                                    setCurJti(row?.jti);
                                    revokeHandleOpen();
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  )}
                </>
              )}
            </Table>
          </TableContainer>
        </Paper>
      </Box>
      <Modal
        open={revokeOpen}
        onClose={(_, reason) => {
          if (reason !== "backdropClick") {
            revokeHandleClose();
          }
        }}
      >
        <ModalBox sx={{ width: "60vw" }} title="Revoke API Token" handleClose={revokeHandleClose}>
          <Grid container pt={4}>
            <Grid
              item
              xs={12}
              sx={{
                margin: "auto",
                textAlign: "left",
              }}
            >
              Are you sure you want to revoke the API Token?
            </Grid>
            <Grid item xs={12} mt={8} textAlign={"right"}>
              <Button variant="outlined" color="primary" onClick={revokeHandleClose} mr={4}>
                <CloseIcon
                  sx={{
                    marginRight: "4px",
                    fillOpacity: "0.5",
                  }}
                />
                No
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  await configLLMSetting(zoneUid, "revokeAPIToken", { jti: curJti });
                  revokeHandleClose();
                }}
              >
                <CheckIcon
                  sx={{
                    marginRight: "4px",
                  }}
                />
                Yes
              </Button>
            </Grid>
          </Grid>
        </ModalBox>
      </Modal>
      <Modal
        open={viewOpen}
        onClose={(_, reason) => {
          if (reason !== "backdropClick") {
            viewHandleClose();
          }
        }}
      >
        <ModalBox sx={{ width: "60vw" }} title="API Token Generated" handleClose={viewHandleClose}>
          <Grid container spacing={4} mt={2}>
            <Grid item xs={12}>
              <Typography variant="h6">Make sure to copy your LLM API access token now. You won{"'"}t be able to see it again!</Typography>
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row">
                <Typography sx={{ maxWidth: "90%", wordBreak: "break-all" }}>{createdToken}</Typography>
                <Tooltip
                  title={copySuccess ? "Copied!" : "Click to Copy"}
                  leaveDelay={copySuccess ? 500 : 200}
                  onClose={handleOnTooltipClose}
                >
                  <IconButton onClick={handleCopy}>
                    <CopyIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} justifyContent="flex-end" mt={4}>
                <Button variant="outlined" color="secondary" size="modal" onClick={viewHandleClose}>
                  Close
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </ModalBox>
      </Modal>
      <CreateAPITokenModal
        open={createOpen}
        handleClose={createHandleClose}
        viewHandleOpen={viewHandleOpen}
        setCreatedToken={setCreatedToken}
        zoneUid={zoneUid}
      />
      <SnackbarAlert open={snackOpen} onClose={handleSnackClose} severity={success} message={message} />
    </React.Fragment>
  );
}
export default LLMAPITokens;
